import { DataVis_1 as DataVis1, FlowConnection } from '@carbon/icons-react';
import * as go from 'gojs';
import * as React from 'react';
import { useRouteMatch } from 'react-router-dom';
import { Tab, Tabs } from 'vet-bones/design-system/components';

import { ROUTE } from 'src/constants/routes';
import { TEST_IDS } from 'src/constants/testIds';
import { DiagramTransaction } from 'src/containers/ProjectDiagram/constants/transactions';
import { useDiagramSelection } from 'src/containers/ProjectDiagram/hooks/useDiagramSelectionValue';
import { useDiagram } from 'src/containers/ProjectDiagram/hooks/useDiagramValue';
import { commitDiagramWhileSkippingUndoManager } from 'src/containers/ProjectDiagram/utils/commitDiagramModel';
import { isMapping } from 'src/containers/ProjectDiagram/utils/data';
import { getMappingIdForSelection } from 'src/containers/ProjectDiagram/utils/mappingForEntityDrawer';
import { setDiagramSelection } from 'src/containers/ProjectDiagram/utils/setDiagramSelection';
import { entityDrawerSelector } from 'src/selectors/entityDrawer';
import { projectLinkDataArraySelector } from 'src/selectors/projectLinkDataArray';
import { projectMappingsSelector } from 'src/selectors/projectMappings';
import * as copy from 'src/templates/copy';
import { useNavigate } from 'src/utils/hooks/useNavigate';
import { useNavigateMapping } from 'src/utils/hooks/useNavigateMapping';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';
import { getProjectRoute } from 'src/utils/routes/getProjectRoute';

import styles from 'src/containers/ProjectNavbar/projectNavbar.module.scss';

export const ProjectNavbarTabs: React.VFC = () => {
  const { mappingId, projectId } = useRouterParams<'PROJECT_MAPPING'>();
  const diagram = useDiagram();
  const isUsingMappingRoute = Boolean(useRouteMatch(ROUTE.PROJECT_MAPPING));

  const selection = useDiagramSelection();
  const { type: entityDrawerType, entityValue: entityDrawerValue } =
    useReduxSelector(entityDrawerSelector);
  const allMappings = useReduxSelector(projectMappingsSelector(projectId));
  const linkDataArray = useReduxSelector(
    projectLinkDataArraySelector(projectId)
  );

  const navigate = useNavigate();
  const handleNavigateToModel = React.useCallback(() => {
    if (diagram && mappingId) {
      const mapping = diagram.model.linkDataArray.find(
        (linkData) => isMapping(linkData) && linkData.value === mappingId
      );
      const mappingLink = diagram.findLinkForKey(mapping?.key);
      // select mapping, or clear selection
      commitDiagramWhileSkippingUndoManager(
        diagram,
        (_, diagram) =>
          setDiagramSelection(
            mappingLink ? new go.Set([mappingLink as go.Link]) : undefined
          )(diagram),
        DiagramTransaction.SET_DIAGRAM_SELECTION
      );
    }
    navigate(getProjectRoute(projectId));
  }, [diagram, mappingId, navigate, projectId]);

  const navigateMapping = useNavigateMapping();
  const handleNavigateToMapping = React.useCallback(() => {
    if (isUsingMappingRoute) {
      return;
    }

    const { mappingId } = getMappingIdForSelection({
      selection,
      entityDrawerType,
      entityDrawerValue,
      allMappings,
      linkDataArray,
    });

    navigateMapping(mappingId);
  }, [
    allMappings,
    entityDrawerType,
    entityDrawerValue,
    isUsingMappingRoute,
    linkDataArray,
    navigateMapping,
    selection,
  ]);

  return (
    <Tabs size="lg" variant="ghost">
      <Tab
        className={styles.projectNavbarTab}
        leftIcon={DataVis1}
        onClick={handleNavigateToModel}
        selected={!isUsingMappingRoute}
        size="lg"
        testId={TEST_IDS.projectNavbar.toggle.modelViewButton}
      >
        {copy.components.project.navbar.model}
      </Tab>
      <div className="sd-project-navbar-toggle__divider" />
      <Tab
        className={styles.projectNavbarTab}
        leftIcon={FlowConnection}
        onClick={handleNavigateToMapping}
        selected={isUsingMappingRoute}
        size="lg"
        testId={TEST_IDS.projectNavbar.toggle.mappingViewButton}
      >
        {copy.components.project.navbar.mapping}
      </Tab>
    </Tabs>
  );
};
